/**
 * @file AddContact.js
 * @description This file contains the AddContact component, which handles both adding new contacts and updating existing contacts.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Dropdown } from '../helpers/Dropdown';
import Toast from '../ui/toasts/Toast';
import style from './ClientList.module.scss';
import {
    fetchContactTypes,
    fetchContactRoles,
} from '../../services/systemService';
import {
    addClientContact,
    updateClientContact,
} from '../../services/clientService';
import { isValidEmailAddress, isValidPhoneNumber } from '../../utils/validate';
import PhoneInput from 'react-phone-number-input/input';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const AddContact = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId, contactId } = useParams();
    const [contactTypes, setContactTypes] = useState([]);
    const [contactRoles, setContactRoles] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        contactTypeId: 0,
        emailAddress: '',
        phoneNumber: '',
        contactRoleId: 0,
        website: '',
        isMain: false,
        notes: '',
    });

    const isEditMode = Boolean(contactId);
    const contactData = location.state?.contactData;
    const existingTab = location.state?.existingTab;
    const actualClientId = location.state?.clientId || clientId;

    useEffect(() => {
        if (isEditMode && contactData) {
            setFormData({
                firstName: contactData.firstName || '',
                lastName: contactData.lastName || '',
                contactTypeId: contactData.contactTypeId || 0,
                emailAddress: contactData.emailAddress || '',
                phoneNumber: contactData.phoneNumber || '',
                contactRoleId: contactData.contactRoleId || 0,
                website: contactData.website || '',
                isMain: contactData.isMain || false,
                notes: contactData.notes || '',
            });
        }
    }, [isEditMode, contactData]);

    const navigateBack = () => {
        navigate(ROUTE_BUILDERS.clientsView(actualClientId), {
            state: { existingTab: existingTab || 'Contacts' },
        });
    };

    const handleClose = () => {
        navigateBack();
    };

    const transformArray = data => {
        return data.map(item => ({
            value: item.id,
            label: item.name,
        }));
    };

    const getContactTypes = async () => {
        try {
            const data = await fetchContactTypes();
            const transformedArray = transformArray(data);
            setContactTypes(transformedArray);
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    const getContactRoles = async () => {
        try {
            const data = await fetchContactRoles();
            const transformedArray = transformArray(data);
            setContactRoles(transformedArray);
        } catch (error) {
            console.error('Could not fetch contact roles:', error);
        }
    };

    useEffect(() => {
        getContactTypes();
        getContactRoles();
    }, []);

    const handleInputChange = event => {
        const { name, value, type, checked } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleDropdownChange = selectedOption => {
        setFormData(prevData => ({
            ...prevData,
            contactTypeId: selectedOption,
        }));
    };

    const handleRoleChange = selectedOption => {
        setFormData(prevData => ({
            ...prevData,
            contactRoleId: selectedOption,
        }));
    };

    const validateForm = () => {
        if (formData.firstName.trim() === '') {
            Toast.error(getText('msg_6ae3e6ac727a'));
            return false;
        }

        if (formData.lastName.trim() === '') {
            Toast.error(getText('msg_ec032cfad80c'));
            return false;
        }

        if (formData.contactTypeId <= 0) {
            Toast.error(getText('msg_a66ebe012754'));
            return false;
        }

        if (formData.contactRoleId <= 0) {
            Toast.error(getText('msg_4b3ac674d66b'));
            return false;
        }

        if (
            formData.phoneNumber.trim() === '' &&
            formData.emailAddress.trim() === ''
        ) {
            Toast.error(getText('msg_9fd44a384ff1'));
            return false;
        }

        if (
            formData.emailAddress.trim() !== '' &&
            !isValidEmailAddress(formData.emailAddress)
        ) {
            Toast.error(getText('msg_fe7db1d225b1'));
            return false;
        }

        if (
            formData.phoneNumber.trim() !== '' &&
            !isValidPhoneNumber(formData.phoneNumber)
        ) {
            Toast.error(getText('msg_f1d3d4393354'));
            return false;
        }

        return true;
    };

    const handleSave = () => {
        const isValidForm = validateForm();

        if (isValidForm) {
            if (isEditMode) {
                const updatedContactData = {
                    ...contactData,
                    ...formData,
                    id: contactId,
                };

                updateClientContact(updatedContactData, actualClientId)
                    .then(() => {
                        Toast.success(
                            `Contact ${formData.firstName} ${formData.lastName} was updated successfully.`,
                        );
                        navigateBack();
                    })
                    .catch(error => {
                        Toast.error(
                            getText('msg_741e0b2fc584'),
                        );
                        console.error(error);
                    });
            } else {
                addClientContact(formData, actualClientId)
                    .then(() => {
                        Toast.success(
                            `Contact ${formData.firstName} ${formData.lastName} was created successfully.`,
                        );
                        navigateBack();
                    })
                    .catch(error => {
                        Toast.error(
                            getText('msg_405f900f3a72'),
                        );
                        console.error(error);
                    });
            }
        }
    };

    const getSelectedContactType = () => {
        if (formData.contactTypeId > 0) {
            const selectedType = contactTypes.find(
                type => type.value === formData.contactTypeId,
            );
            return selectedType ? selectedType.label : 'Select';
        }
        return 'Select';
    };

    const getSelectedRole = () => {
        if (formData.contactRoleId > 0) {
            const selectedRole = contactRoles.find(
                role => role.value === formData.contactRoleId,
            );
            return selectedRole ? selectedRole.label : 'Select';
        }
        return 'Select';
    };

    return (
        <div
            className="page-content"
            style={{ height: '100%', overflow: 'auto' }}
        >
            <div className="card" style={{ minHeight: '100%' }}>
                <div className="card-body px-0 py-4">
                    {/* Page Header with Title and Action */}
                    <div className="d-flex justify-content-between">
                        {/* Page Title */}
                        <p className="page-title float-start d-inline card-title ms-4">
                                                        {isEditMode
                                                                ? `${formData.firstName} ${formData.lastName}`.trim() ||
                                                                    'Update Contact'
                                                                : 'Add Contact'}
                        </p>
                        {/* Page Actions */}
                        <div className="float-end d-flex align-items-center me-4">
                            <p
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons d-inline ms-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </p>
                        </div>
                    </div>
                    <hr style={{ margin: '16px 0px 45px 0px' }} />

                    <form className={style.demographicFormContainer}>
                        <div className={`row px-5 pb-5`}>
                            <div
                                className={`col-12 col-xs-12 col-md-12 col-lg-12 col-xl-6`}
                                style={{ margin: '0 auto' }}
                            >
                                <div className={`form-group mb-3 flex-wrap`}>
                                    <div
                                        className={`form-group mb-3 inline-input`}
                                    >
                                        <div className="col-12 col-xs-12 col-md-12 col-lg-4 me-4">
                                            <label htmlFor="firstName">
                                                {getText('msg_b6ea992aab46')}
                                                <span className="text-danger">
                                                    {' '}
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                id="firstName"
                                                type="text"
                                                maxLength="50"
                                                className={`form-control`}
                                                value={formData.firstName || ''}
                                                onChange={handleInputChange}
                                                name="firstName"
                                                placeholder={getText('msg_0f6fa8fa8048')}
                                            />
                                        </div>
                                        <div className="col-12 col-xs-12 col-md-12 col-lg-4">
                                            <label htmlFor="lastName">
                                                {getText('msg_863cb39fbe7d')}
                                                <span className="text-danger">
                                                    {' '}
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                id="lastName"
                                                type="text"
                                                maxLength="50"
                                                className={`form-control`}
                                                value={formData.lastName || ''}
                                                onChange={handleInputChange}
                                                name="lastName"
                                                placeholder={getText('msg_9efa5a4810e3')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="phoneNumber">
                                        <i
                                            className="bi bi-telephone"
                                            style={{ marginRight: '10px' }}
                                        ></i>
                                        {getText('msg_77064d526523')}
                                    </label>
                                    <PhoneInput
                                        id="phoneNumber"
                                        className="form-control"
                                        value={formData.phoneNumber || ''}
                                        onChange={value =>
                                            setFormData(prevData => ({
                                                ...prevData,
                                                phoneNumber: value,
                                            }))
                                        }
                                        name="phoneNumber"
                                        country="US"
                                    />
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="emailAddress">
                                        <i
                                            className="bi bi-envelope"
                                            style={{ marginRight: '10px' }}
                                        ></i>
                                        {getText('msg_84add5b29527')}
                                    </label>
                                    <input
                                        id="emailAddress"
                                        type="text"
                                        maxLength="50"
                                        className={`form-control`}
                                        value={formData.emailAddress || ''}
                                        onChange={handleInputChange}
                                        name="emailAddress"
                                        placeholder={getText('msg_3ce7dd24ce64')}
                                    />
                                    <p className="mt-2 mb-0">
                                        <span className="text-danger">*</span>{' '}
                                        {getText('msg_776a2c441364')}
                                    </p>
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="website">{getText('msg_2e8a57cc5c47')}</label>
                                    <input
                                        id="website"
                                        type="text"
                                        maxLength="200"
                                        className={`form-control`}
                                        value={formData.website || ''}
                                        onChange={handleInputChange}
                                        name="website"
                                        placeholder={getText('msg_9e96fc5a930c')}
                                    />
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="uniqueId">
                                        {getText('msg_fcbedcab55ea')}
                                        <span className="text-danger"> *</span>
                                    </label>
                                    <Dropdown
                                        defaultValue={getSelectedContactType()}
                                        options={contactTypes}
                                        onChange={handleDropdownChange}
                                    />
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="jobTitle">
                                        {getText('msg_c3f104d13657')}
                                        <span className="text-danger"> *</span>
                                    </label>
                                    <Dropdown
                                        defaultValue={getSelectedRole()}
                                        options={contactRoles}
                                        onChange={handleRoleChange}
                                    />
                                </div>

                                <div
                                    className={`form-group mb-3 flex-wrap py-3`}
                                >
                                    <div className="form-check">
                                        <input
                                            id="isMain"
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={formData.isMain}
                                            onChange={handleInputChange}
                                            name="isMain"
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="isMain"
                                        >
                                            {getText('msg_a9a96ec01949')}
                                        </label>
                                    </div>
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <label htmlFor="notes">{getText('msg_70440046a3dc')}</label>
                                    <textarea
                                        id="notes"
                                        className="form-control"
                                        value={formData.notes || ''}
                                        onChange={handleInputChange}
                                        name="notes"
                                        placeholder=""
                                        rows="5"
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>

                                <div className={`form-group mb-3 flex-wrap`}>
                                    <p>
                                        <span className="text-danger">*</span>{' '}
                                        {getText('msg_7c118f042eaa')}
                                    </p>
                                    <div
                                        onClick={handleSave}
                                        type="button"
                                        style={{
                                            display: 'block',
                                            height: '40px',
                                            width: '128px',
                                            textAlign: 'center',
                                            marginTop: '38px',
                                            marginBottom: '38px',
                                        }}
                                        className="float-end button-base ing-button-primary-left"
                                    >
                                        {isEditMode ? 'Update' : 'Save'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddContact;
